import React, { useEffect, useState } from 'react';
import API from '../../services/api';
import { Link } from 'react-router-dom';
import Icon from '../ui/Icon';
import './PlatformOverview.css';

const READINESS_LABEL = { needs_improvement: 'Needs improvement', moderate: 'Moderate', placement_ready: 'Placement ready', top_performer: 'Top performer' };
const READINESS_ORDER = ['needs_improvement', 'moderate', 'placement_ready', 'top_performer'];
const cap = (s) => String(s).replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
const num = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-IN'));

function Bars({ rows, total }) {
  if (!rows?.length) return <div className="po-empty">No data yet</div>;
  const sum = total ?? rows.reduce((s, r) => s + r.count, 0);
  return (
    <ul className="po-bars">
      {rows.map(r => (
        <li key={r.label}>
          <span className="po-bar-label">{cap(r.label)}</span>
          <span className="po-bar-track"><span className="po-bar-fill" style={{ width: `${sum ? (r.count / sum) * 100 : 0}%` }} /></span>
          <span className="po-bar-val">{num(r.count)}{sum ? <em> {Math.round((r.count / sum) * 100)}%</em> : null}</span>
        </li>
      ))}
    </ul>
  );
}

function Kpi({ icon, label, value, sub }) {
  return (
    <div className="po-kpi">
      <span className="po-kpi-icon"><Icon name={icon} size={20} /></span>
      <div><div className="po-kpi-val">{value}</div><div className="po-kpi-label">{label}</div>{sub && <div className="po-kpi-sub">{sub}</div>}</div>
    </div>
  );
}

/** Super-admin platform overview — every number is a live aggregate from /colleges/platform-analytics. */
export default function PlatformOverview() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const load = () => {
    setErr(''); setD(null);
    API.get('/colleges/platform-analytics').then(r => setD(r.data)).catch(e => setErr(e.response?.data?.message || 'We could not load platform analytics.'));
  };
  useEffect(load, []);

  if (err) return <div className="po-error" role="alert">{err} <button type="button" onClick={load}>Retry</button></div>;
  if (!d) return <div className="po-empty">Loading platform analytics…</div>;

  const readiness = READINESS_ORDER.map(l => ({ label: READINESS_LABEL[l], count: d.readiness.find(r => r.level === l)?.count || 0 }));
  const readinessTotal = readiness.reduce((s, r) => s + r.count, 0);
  const maxGrowth = Math.max(1, ...d.growth.map(g => Math.max(g.new_students, g.attempts)));

  return (
    <div className="po">
      <div className="po-kpis">
        <Kpi icon="building" label="Colleges" value={num(d.colleges.total)} sub={`${num(d.colleges.active)} active`} />
        <Kpi icon="users" label="Students" value={num(d.people.students)} sub={`${num(d.people.new_students_30d)} joined in 30 days`} />
        <Kpi icon="profile" label="College admins" value={num(d.people.admins)} />
        <Kpi icon="book" label="Active questions" value={num(d.questions.active)} sub={`${num(d.questions.total)} in total`} />
        <Kpi icon="test" label="Assessments" value={num(d.usage.tests_total)} />
        <Kpi icon="check" label="Submitted attempts" value={num(d.usage.attempts_submitted)} sub={`${num(d.usage.attempts_30d)} in 30 days`} />
        <Kpi icon="streak" label="Active students (30d)" value={num(d.usage.active_students_30d)} />
        <Kpi icon="chart" label="Average score" value={d.usage.avg_score_pct === null ? '—' : `${d.usage.avg_score_pct}%`} sub="earned ÷ maximum marks" />
      </div>

      <div className="po-grid">
        <section className="po-card"><h3>Questions by difficulty</h3><Bars rows={d.questions.by_difficulty} /></section>
        <section className="po-card"><h3>Questions by category</h3><Bars rows={d.questions.by_category} /></section>
        <section className="po-card"><h3>Questions by source</h3><Bars rows={d.questions.by_source} /></section>
        <section className="po-card">
          <h3>Question bank ownership</h3>
          <div className="po-split">
            <div><b>{num(d.questions.platform_bank)}</b><span>Platform bank</span></div>
            <div><b>{num(d.questions.college_contributed)}</b><span>College-contributed</span></div>
            <div><b>{num(d.questions.pending_review)}</b><span>Pending review</span></div>
          </div>
          {d.questions.pending_review > 0 && <p className="po-note"><Link to="/super-admin/review">Review {d.questions.pending_review} pending submission{d.questions.pending_review === 1 ? "" : "s"}</Link></p>}
        </section>
        <section className="po-card">
          <h3>Student readiness</h3>
          {readinessTotal === 0 ? <div className="po-empty">No readiness profiles yet — they appear after students complete diagnostic assessments.</div> : <Bars rows={readiness} total={readinessTotal} />}
        </section>
        <section className="po-card">
          <h3>Growth, last 6 months</h3>
          <div className="po-growth" role="img" aria-label="New students and submitted attempts per month">
            {d.growth.map(g => (
              <div className="po-growth-col" key={g.month}>
                <div className="po-growth-bars">
                  <span className="po-g po-g--s" style={{ height: `${(g.new_students / maxGrowth) * 100}%` }} title={`${g.new_students} new students`} />
                  <span className="po-g po-g--a" style={{ height: `${(g.attempts / maxGrowth) * 100}%` }} title={`${g.attempts} attempts`} />
                </div>
                <span className="po-growth-m">{g.month.slice(5)}</span>
              </div>
            ))}
          </div>
          <div className="po-legend"><span><i className="po-dot po-g--s" />New students</span><span><i className="po-dot po-g--a" />Submitted attempts</span></div>
        </section>
      </div>

      <section className="po-card">
        <h3>College-wise</h3>
        <div className="po-table-wrap">
          <table className="po-table">
            <thead><tr><th>College</th><th>Students</th><th>Admins</th><th>Assessments</th><th>Attempts</th><th>Avg score</th><th>Questions contributed</th></tr></thead>
            <tbody>
              {d.by_college.map(c => (
                <tr key={c.id}>
                  <td>{c.name}{!c.is_active && <span className="po-tag">inactive</span>}</td>
                  <td>{num(c.students)}</td><td>{num(c.admins)}</td><td>{num(c.tests)}</td><td>{num(c.attempts)}</td>
                  <td>{c.avg_score_pct === null ? '—' : `${c.avg_score_pct}%`}</td><td>{num(c.questions_contributed)}</td>
                </tr>
              ))}
              {d.by_college.length === 0 && <tr><td colSpan={7} className="po-empty">No colleges yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
